import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { useAuth } from '@deloitte-us-consulting/gp-sdk';

export type UsageTarget = 'home' | `app:${string}` | `tool:${string}`;

/** One app or tool's opens in this browser: how many, and when last (a counter, not a clock). */
export interface LocalUsageEntry {
  count: number;
  seq: number;
}

export type LocalUsageMap = Record<string, LocalUsageEntry>;

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const USAGE_STORAGE_KEY = 'gp.appStore.usage.v1';

const listeners = new Set<() => void>();

function isEntry(value: unknown): value is LocalUsageEntry {
  if (!value || typeof value !== 'object') return false;
  const { count, seq } = value as Record<string, unknown>;
  return typeof count === 'number' && Number.isFinite(count) && count > 0 && typeof seq === 'number' && Number.isFinite(seq);
}

function parseUsage(raw: string | null): LocalUsageMap {
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.fromEntries(Object.entries(parsed).filter(([, v]) => isEntry(v))) as LocalUsageMap;
  } catch {
    return {};
  }
}

function readRaw(): string | null {
  try {
    return window.localStorage.getItem(USAGE_STORAGE_KEY);
  } catch {
    return null;
  }
}

/** This browser's app and tool opens, for the launcher's Quick Launch. Malformed entries are dropped. */
export function readLocalUsage(): LocalUsageMap {
  return parseUsage(readRaw());
}

/**
 * Count one app or tool open in this browser. Only trackAccess calls it, so an open
 * is counted exactly where the server counts it: a hosted app when RemoteRoute mounts
 * it (in whichever tab it opens in), a linked app or a tool on its tile's click.
 */
function recordLocalUsage(target: UsageTarget): void {
  if (target === 'home') return;
  try {
    const map = readLocalUsage();
    const seq = Math.max(0, ...Object.values(map).map((e) => e.seq)) + 1;
    map[target] = { count: (map[target]?.count ?? 0) + 1, seq };
    window.localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(map));
    listeners.forEach((fn) => fn());
  } catch {
    // Storage failures (private mode / quota) must never affect navigation.
  }
}

function subscribe(onChange: () => void): () => void {
  // Another tab's write arrives as a storage event; `key` is null when it cleared storage.
  const onStorage = (e: StorageEvent) => {
    if (e.key === USAGE_STORAGE_KEY || e.key === null) onChange();
  };
  listeners.add(onChange);
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(onChange);
    window.removeEventListener('storage', onStorage);
  };
}

// useSyncExternalStore needs the same object back until the stored value changes.
let snapshot: { raw: string | null; usage: LocalUsageMap } = { raw: null, usage: {} };
function getSnapshot(): LocalUsageMap {
  const raw = readRaw();
  if (raw !== snapshot.raw) snapshot = { raw, usage: parseUsage(raw) };
  return snapshot.usage;
}

/** This browser's opens, re-rendering when an open is counted in this tab or another one. */
export function useLocalUsage(): LocalUsageMap {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Record one App Store access, fire-and-forget. `keepalive` lets the request outlive
 * a tab that is navigating away (an external tile opens a new tab, but a hosted app
 * leaves this page). Recording must never affect the page, so nothing here throws,
 * logs, retries or surfaces a failure: a 400/401/403 (including the API's
 * impersonation guardrail) is ignored the same as a network error.
 */
export function trackAccess(target: UsageTarget, token: string | null | undefined): void {
  if (!token) return;
  recordLocalUsage(target);
  try {
    fetch(`${API_URL}/api/usage/access`, {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ target }),
    }).catch(() => {});
  } catch {
    // A synchronous failure is ignored for the same reason.
  }
}

/**
 * Record `target` once per mount per target. It waits for a token, and a later token
 * refresh does not re-record. A null target records nothing.
 */
export function useTrackAccess(target: UsageTarget | null): void {
  const { accessToken } = useAuth();
  const fired = useRef<UsageTarget | null>(null);
  useEffect(() => {
    if (!target || !accessToken || fired.current === target) return;
    fired.current = target;
    trackAccess(target, accessToken);
  }, [target, accessToken]);
}

/** A stable click recorder for tiles. It never changes the click's navigation. */
export function useTrackClick(): (target: UsageTarget) => void {
  const { accessToken } = useAuth();
  return useCallback((target: UsageTarget) => trackAccess(target, accessToken), [accessToken]);
}
